import { useEffect, useRef, useState } from "react";
import { useSnapshot } from "valtio";
import { localState } from "~/state/state";
import { createPortal } from "react-dom";
import { styled, keyframes, vh, darkTheme } from "~/ui/style/stitches.config";

import { getTarotCards, TarotCard } from "~/api/communityApi";
import { createJournalEntry, JournalEntry } from "~/api/journalApi";
import { Button } from "~/ui/components/Button";
import { SimpleLoadingIndicator } from "~/pages/loading/LoadingPage";
import { trackEvent } from "~/api/analyticsApi";
import { motion } from "framer-motion";
import { expandSearchTerms } from "~/utils/searchUtils";
import { sendToastMessageWithFallbackCode } from "~/utils/handleToasts";
import { imageUrl } from "~/utils/imageurl";
import { isIOSNativeApp } from "~/utils/deviceDetection";

const fadeIn = keyframes({
  from: { opacity: 0 },
  to: { opacity: 1 },
});

const getDeckSlugFromPath = (imagePath: string): string => {
  if (!imagePath?.includes('/decks/')) return 'rider-waite';
  const folderName = imagePath.split('/decks/')[1]?.split('/')[0];
  return folderName || 'rider-waite';
};

interface SelectedCard {
  cardId: string;
  name: string;
  imagePath: string;
  position: string;
  isReversed: boolean;
  keywords: string[];
  keywordsReverse: string[];
}

interface CreateJournalEntryOverlayProps {
  onClose: () => void;
  onCreated: (entry: JournalEntry) => void;
}

export const CreateJournalEntryOverlay = ({
  onClose,
  onCreated,
}: CreateJournalEntryOverlayProps) => {
  const { appDarkMode: dk } = useSnapshot(localState);
  const [cards, setCards] = useState<TarotCard[]>([]);
  const [loadingCards, setLoadingCards] = useState(true);
  const [selectedCards, setSelectedCards] = useState<SelectedCard[]>([]);
  const [query, setQuery] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedSuit, setSelectedSuit] = useState<string | null>("major");
  const [loadedSuits, setLoadedSuits] = useState<Set<string>>(new Set());
  const [editingPositionIndex, setEditingPositionIndex] = useState<number | null>(null);
  const [deckName, setDeckName] = useState("rider-waite");

  const overlayRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const fetchCards = async () => {
      try {
        const data = await getTarotCards();
        setCards(data);

        const majorCards = data.filter(c => c.category === "major");
        await Promise.all(
          majorCards.map(card => {
            if (!card.imagePath) return Promise.resolve();
            return new Promise<void>((resolve) => {
              const img = new Image();
              img.onload = () => resolve();
              img.onerror = () => resolve();
              img.src = imageUrl(card.imagePath!, 100);
            });
          })
        );
        setLoadedSuits(new Set(["major"]));
      } catch (err) {
        console.error("Failed to fetch cards:", err);
      } finally {
        setLoadingCards(false);
      }
    };
    fetchCards();
  }, []);

  const filteredCards = cards.filter(card => {
    const cardNameLower = card.name.toLowerCase();
    const expandedTerms = expandSearchTerms(searchTerm);
    return expandedTerms.some(term => cardNameLower.includes(term));
  });

  const groupedCards = {
    major: filteredCards.filter(c => c.category === "major"),
    wands: filteredCards.filter(c => c.category === "wands"),
    cups: filteredCards.filter(c => c.category === "cups"),
    swords: filteredCards.filter(c => c.category === "swords"),
    pentacles: filteredCards.filter(c => c.category === "pentacles"),
  };

  const handleSuitClick = async (suit: string) => {
    if (selectedSuit === suit) return;
    setSelectedSuit(suit);
    if (loadedSuits.has(suit)) return;

    const suitCards = cards.filter(c => c.category === suit);
    await Promise.all(
      suitCards.map(card => {
        if (!card.imagePath) return Promise.resolve();
        return new Promise<void>((resolve) => {
          const img = new Image();
          img.onload = () => resolve();
          img.onerror = () => resolve();
          img.src = imageUrl(card.imagePath!, 100);
        });
      })
    );
    setLoadedSuits(prev => new Set(prev).add(suit));
  };

  const addCard = (card: TarotCard) => {
    if (selectedCards.length >= 10) return;
    setSelectedCards([...selectedCards, {
      cardId: card.id,
      name: card.name,
      imagePath: card.imagePath || "",
      position: "",
      isReversed: false,
      keywords: card.keywords || [],
      keywordsReverse: card.keywordsReverse || [],
    }]);
    setSearchTerm("");
    if (selectedCards.length === 0 && card.imagePath) {
      setDeckName(getDeckSlugFromPath(card.imagePath));
    }
  };

  const removeCard = (index: number) => {
    setSelectedCards(selectedCards.filter((_, i) => i !== index));
  };

  const updateCard = (index: number, updates: Partial<SelectedCard>) => {
    setSelectedCards(selectedCards.map((card, i) =>
      i === index ? { ...card, ...updates } : card
    ));
  };

  const handleSaveClick = () => {
    if (selectedCards.length === 0) {
      sendToastMessageWithFallbackCode("Please add at least one card", true, 0);
      return;
    }
    handleSubmit();
  };

  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      const entry = await createJournalEntry({
        cards: selectedCards.map(card => ({
          name: card.name,
          imagePath: card.imagePath ? imageUrl(card.imagePath, 400) : "",
          position: card.position,
          isReversed: card.isReversed,
          keywords: card.isReversed && card.keywordsReverse.length > 0
            ? card.keywordsReverse
            : card.keywords,
        })),
        query: query.trim(),
        deckName,
      });
      trackEvent("/journal/created_manual_entry");
      onCreated(entry);
    } catch (err: any) {
      sendToastMessageWithFallbackCode(err.response?.data?.error || "Failed to save journal entry", true, 0);
    } finally {
      setSubmitting(false);
    }
  };

  return createPortal(
    <StyledOverlay
      as={motion.div}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
    >
      <StyledOverlayContent
        ref={overlayRef}
        as={motion.div}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 20 }}
        onClick={(e) => e.stopPropagation()}
        isIOSNative={isIOSNativeApp()}
      >
        <div className="sticky-header">
          <img
            className="x-button"
            onClick={onClose}
            src="/images/exit.svg"
            style={{
              width: '28px',
              height: '28px',
              cursor: 'pointer',
              opacity: 1,
              transition: 'opacity 0.2s ease',
              userSelect: 'none',
            }}
            onMouseOver={e => e.currentTarget.style.opacity = '0.6'}
            onMouseOut={e => e.currentTarget.style.opacity = '1'}
          />
        </div>

        <div className="overlay-body">
          {/* Question input */}
          <div style={{ marginBottom: '30px', width: '100%' }}>
            <h2 className="question-heading" style={{
              marginBottom: '12px',
              fontSize: '25px',
              fontFamily: 'KeplerLightCondensed',
              marginTop: '.5rem',
              fontWeight: 100,
            }}>Your question</h2>
            <textarea
              className="question-input"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="What are you exploring?"
              maxLength={300}
              style={{
                width: '100%',
                minHeight: '80px',
                backgroundColor: dk ? '#000000' : '#fff',
                border: dk ? '1px solid #505050' : '1px solid #ddd',
                borderRadius: '12px',
                padding: '12px',
                color: dk ? '#fff' : '#000',
                fontSize: '12px',
                lineHeight: '1.4',
                resize: 'none',
                outline: 'none',
                transition: 'border-color 0.2s ease',
                boxSizing: 'border-box',
              }}
              onFocus={(e) => e.target.style.borderColor = dk ? '#aaaaaa' : '#888888'}
              onBlur={(e) => e.target.style.borderColor = dk ? '#505050' : '#ddd'}
            />
          </div>

          {/* Cards section */}
          <div className="form-section">
            <h2 style={{
              marginBottom: '12px',
              fontSize: '11px',
              textTransform: 'uppercase',
              marginTop: '.5rem',
              fontWeight: 400,
            }}>Cards ({selectedCards.length}/10)</h2>

            {/* Selected cards */}
            {selectedCards.length > 0 && (
              <>
              <div className="selected-cards">
                {selectedCards.map((card, index) => (
                  <div key={index} className="selected-card">
                    <div className="card-row">
                      <div className="card-preview">
                        {card.imagePath && (
                          <img
                            src={imageUrl(card.imagePath, 100)}
                            alt={card.name}
                            style={{ transform: card.isReversed ? 'rotate(180deg)' : 'none' }}
                          />
                        )}
                        <button className="remove-card" onClick={() => removeCard(index)}>×</button>
                      </div>
                      <button
                        className={`reverse-button ${card.isReversed ? 'active' : ''}`}
                        onClick={() => updateCard(index, { isReversed: !card.isReversed })}
                        title={card.isReversed ? 'Set upright' : 'Set reversed'}
                      >
                        <img
                          src="/images/room/reverse-tool.svg"
                          alt="Reverse"
                          style={{ transform: card.isReversed ? 'scaleY(1)' : 'scaleY(-1)' }}
                        />
                      </button>
                    </div>
                    <div className="card-name">{card.name}{card.isReversed ? ' (R)' : ''}</div>
                    {editingPositionIndex === index || !card.position ? (
                      <input
                        type="text"
                        className="position-input"
                        value={card.position}
                        onChange={(e) => updateCard(index, { position: e.target.value })}
                        placeholder="Slot name"
                        maxLength={50}
                        autoFocus={editingPositionIndex === index}
                        onFocus={() => setEditingPositionIndex(index)}
                        onBlur={() => setEditingPositionIndex(null)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            (e.target as HTMLInputElement).blur();
                          }
                        }}
                      />
                    ) : (
                      <div
                        className="position-display"
                        onClick={() => setEditingPositionIndex(index)}
                      >
                        {card.position}
                      </div>
                    )}
                  </div>
                ))}
              </div>
              {!selectedCards.some(card => card.position.trim()) && (
                <p style={{
                  fontSize: '10px',
                  color: '#999',
                  marginTop: '-10px',
                  marginLeft: '6px',
                  marginBottom: '16px',
                }}>Positions (e.g. Situation, Obstacle, Advice) help with interpretations.</p>
              )}
              </>
            )}

            {/* Search input */}
            {selectedCards.length < 10 && (
              <div className="search-container">
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                  }}
                  placeholder="Search for a card..."
                  className="card-search"
                />
                <div
                  className={`clear-icon ${searchTerm ? "visible" : ""}`}
                  onClick={() => setSearchTerm("")}
                />
              </div>
            )}

            {/* Card picker */}
            {selectedCards.length < 10 && (
              <>
              {loadingCards ? (
                <div className="loading-cards">
                  <SimpleLoadingIndicator style={{ position: 'static', width: '20px', height: '20px' }} />
                </div>
              ) : (
                <div className="card-picker">
                  <div className="suit-buttons">
                    {["major", "wands", "cups", "swords", "pentacles"].map(suit => (
                      <button
                        key={suit}
                        className={`suit-button ${selectedSuit === suit ? "active" : ""}`}
                        onClick={() => handleSuitClick(suit)}
                      >
                        {suit === "major" ? "Major" : suit === "pentacles" ? "Pents" : suit.charAt(0).toUpperCase() + suit.slice(1)}
                      </button>
                    ))}
                  </div>

                  <motion.div
                    key={selectedSuit || "all"}
                    className={`category-cards ${selectedSuit && loadedSuits.has(selectedSuit) ? "loaded" : ""}`}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.15 }}
                  >
                    {(searchTerm
                      ? filteredCards
                      : selectedSuit
                        ? groupedCards[selectedSuit as keyof typeof groupedCards] || []
                        : []
                    ).map(card => (
                      <button
                        key={card.id}
                        className="card-option"
                        onClick={() => addCard(card)}
                        disabled={selectedCards.some(sc => sc.cardId === card.id)}
                      >
                        {card.imagePath && (
                          <img
                            src={imageUrl(card.imagePath, 100)}
                            alt={card.name}
                          />
                        )}
                        <span>{card.name}</span>
                      </button>
                    ))}
                  </motion.div>
                </div>
              )}
              </>
            )}
          </div>
        </div>

        <div className="overlay-footer">
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '12px',
              opacity: 0,
              animation: `${fadeIn} 0.5s ease forwards`,
              animationDelay: '0.12s',
            }}
          >
            <Button
              Theme={dk ? "dark" : "light"}
              onClick={onClose}
              style={{
                padding: '10px 20px',
                border: dk ? '1px solid rgba(255,255,255,0.6)' : '1px solid #000',
                borderRadius: '12px',
                fontSize: '12px',
                minWidth: '90px',
              }}
            >
              Cancel
            </Button>
            <Button
              Theme={dk ? "light" : "dark"}
              onClick={handleSaveClick}
              disabled={submitting}
              style={{
                padding: '10px 20px',
                border: dk ? '1px solid rgba(255,255,255,0.6)' : '1px solid #000',
                borderRadius: '12px',
                fontSize: '12px',
                minWidth: '90px',
              }}
            >
              {submitting ? (
                <SimpleLoadingIndicator style={{
                  width: "14px",
                  height: "14px",
                  position: "relative",
                  top: "0px",
                  opacity: "0.6"
                }} />
              ) : "Save"}
            </Button>
          </div>
        </div>
      </StyledOverlayContent>
    </StyledOverlay>,
    document.body
  );
};


const StyledOverlay = styled("div", {
  position: "fixed",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: "rgba(180, 180, 180, 0.4)",
  [`.${darkTheme.className} &`]: { backgroundColor: "rgba(0, 0, 0, 0.6)" },
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  zIndex: 999999,

  "@mobile": {
    backgroundColor: "transparent !important",
    height: "-webkit-fill-available",
  },
});

const StyledOverlayContent = styled("div", {
  backgroundColor: "$wash",
  borderRadius: "24px",
  border: "1px solid $gray600",
  padding: "32px",
  maxWidth: "460px",
  width: "90%",
  maxHeight: vh(84),
  minHeight: vh(50),
  position: "relative",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  overflowY: "scroll",
  overflowX: "hidden",
  "&::-webkit-scrollbar": {
    width: "6px",
  },
  "&::-webkit-scrollbar-thumb": {
    borderRadius: "6px",
    backgroundColor: "rgba(127, 127, 127, .5)",
  },
  "&::-webkit-scrollbar-track": {
    backgroundColor: "transparent",
    marginTop: "24px",
    marginBottom: "24px",
  },
  "&::-webkit-scrollbar-corner": {
    backgroundColor: "transparent",
  },

  "& .sticky-header": {
    position: "sticky",
    top: "0px",
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    padding: "0px 10px 30px 10px",
    zIndex: 4,
    width: "calc(100% + 64px)",
    marginLeft: "-32px",
    marginRight: "-32px",
    marginTop: "-32px",
    boxSizing: "border-box",
    borderTopLeftRadius: "24px",
    borderTopRightRadius: "24px",
  },

  "@mobile": {
    border: "none !important",
    borderRadius: 0,
    width: "100%",
    height: "100%",
    maxWidth: "none",
    maxHeight: "none !important",
    minHeight: `calc(${vh(100)} - 140px)`,
    margin: 0,
    paddingTop: "24px !important",

    "& .sticky-header": {
      borderTopLeftRadius: 0,
      borderTopRightRadius: 0,
    },
  },

  "& .overlay-body": {
    flex: 1,
    width: "100%",
    paddingBottom: "100px",
    "@mobile": {
      paddingBottom: "120px",
    },
  },

  "& .question-heading": {
    "@mobile": {
      fontSize: "26px !important",
    },
  },

  "& .question-input": {
    "@mobile": {
      fontSize: "13px !important",
    },
  },

  "& .form-section": {
    marginBottom: "20px",

    "& label": {
      display: "block",
      fontSize: "12px",
      fontWeight: "500",
      marginBottom: "8px",
      color: "#333",
    },

    "& textarea": {
      width: "100%",
      padding: "12px",
      border: "1px solid #ddd",
      borderRadius: "10px",
      fontSize: "13px",
      fontFamily: "inherit",
      resize: "none",

      "&:focus": {
        outline: "none",
        borderColor: "#000",
      },
    },
  },

  "& .selected-cards": {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: "16px 16px",
    width: "100%",
    marginBottom: "16px",
  },

  "& .selected-card": {
    textAlign: "center",

    "& .card-row": {
      position: "relative",
      display: "flex",
      justifyContent: "center",
      marginBottom: "4px",
    },

    "& .card-preview": {
      position: "relative",

      "& img": {
        width: "60px",
        height: "auto",
        borderRadius: "4px",
        border: "1px solid #ddd",
        userSelect: "none",
      },

      "& .remove-card": {
        position: "absolute",
        top: "-6px",
        right: "-6px",
        width: "18px",
        height: "18px",
        borderRadius: "50%",
        backgroundColor: "#000",
        color: "#e5e5e5",
        border: "none",
        fontSize: "13px",
        fontWeight: "200",
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      },
    },

    "& .reverse-button": {
      position: "absolute",
      right: "-2px",
      top: "50%",
      transform: "translateY(-50%)",
      background: "transparent",
      border: "none",
      borderRadius: "6px",
      cursor: "pointer",
      padding: "4px",
      opacity: 0.4,
      transition: "opacity 0.2s ease, background 0.2s ease",

      "@mobile": {
        right: "-8px",
      },

      "&:hover": {
        opacity: 0.7,
        background: "rgba(0, 0, 0, 0.05)",
      },

      "& img": {
        width: "18px",
        height: "18px",
        display: "block",
      },
    },

    "& .card-name": {
      fontSize: "10px",
      fontWeight: "400",
      marginBottom: "6px",
      textTransform: "uppercase",
    },

    "& .position-input": {
      width: "100%",
      height: "28px",
      padding: "2px 10px",
      fontSize: "11px",
      borderRadius: "8px",
      marginBottom: "4px",
      border: "1px solid #ddd",
      "&:focus": {
        outline: "none",
        borderColor: "#000",
      },
    },

    "& .position-display": {
      fontSize: "10px",
      color: "rgb(0, 0, 0, 0.5)",
      letterSpacing: "0.01em",
      cursor: "pointer",
      padding: "4px 10px",
      marginTop: "-6px",
      borderRadius: "20px",
      transition: "background 0.2s ease",

      "&:hover": {
        color: "rgba(0, 0, 0, .3)",
      },
    },
  },

  "& .search-container": {
    position: "relative",
    width: "100%",
    marginBottom: "12px",
  },

  "& .card-search": {
    width: "100%",
    padding: "12px",
    paddingRight: "30px",
    border: "1px solid #ddd",
    borderRadius: "12px",
    fontSize: "12px",
    fontFamily: "inherit",

    "@mobile": {
      fontSize: "13px",
    },

    "&:focus": {
      outline: "none",
      borderColor: "#000",
    },
  },

  "& .clear-icon": {
    position: "absolute",
    right: "8px",
    top: "50%",
    transform: "translateY(-50%)",
    width: "16px",
    height: "16px",
    cursor: "pointer",
    display: "none",
    alignItems: "center",
    justifyContent: "center",
    backgroundImage: "url(/images/exit.svg)",
    backgroundSize: "contain",
    backgroundRepeat: "no-repeat",
    backgroundPosition: "center",
    opacity: 0.6,
    transition: "opacity 0.2s",

    "&.visible": {
      display: "flex",
    },

    "&:hover": {
      opacity: 0.7,
    },
  },

  "& .loading-cards": {
    display: "flex",
    justifyContent: "center",
    padding: "20px",
  },

  "& .card-picker": {
    width: "100%",
  },

  "& .category-cards": {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(70px, 1fr))",
    gap: "8px",
    opacity: 0,
    transition: "opacity 0.25s ease",
    minHeight: "300px",
    alignContent: "start",

    "&.loaded": {
      opacity: 1,
    },
  },

  "& .card-option": {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: "10px",
    border: "1px solid rgba(0, 0, 0, 0.15)",
    borderRadius: "8px",
    backgroundColor: "#fff",
    cursor: "pointer",
    transition: "all 0.2s",

    "&:hover:not(:disabled)": {
      border: "1px solid rgba(0, 0, 0, 0.15)",
      backgroundColor: "#ebebeb",
    },

    "&:disabled": {
      opacity: 0.4,
      cursor: "not-allowed",
    },

    "& img": {
      width: "40px",
      height: "auto",
      borderRadius: "3px",
      marginBottom: "4px",
    },

    "& span": {
      fontSize: "9px",
      textAlign: "center",
      lineHeight: 1.2,
    },
  },

  "& .suit-buttons": {
    display: "flex",
    flexWrap: "wrap",
    gap: "6px",
    justifyContent: "center",
    margin: "4px 0px 18px 0px",
  },

  "& .suit-button": {
    padding: "0px 9px",
    height: "26px",
    fontSize: "10px",
    fontWeight: "200",
    color: "rgba(0, 0, 0, 0.8)",
    letterSpacing: "0.02em",
    textTransform: "uppercase",
    border: "1px solid rgba(0, 0, 0, 0.05)",
    borderRadius: "16px",
    backgroundColor: "#e6e6e6",
    cursor: "pointer",
    transition: "all 0.2s",

    "@notmobile": {
      width: "70px",
    },

    "&:hover": {
      backgroundColor: "#dadada",
    },

    "&.active": {
      backgroundColor: "#cfcfcf",
      color: "#000",
      border: "1px solid rgba(0, 0, 0, 0.25)",
    },
  },

  "& .overlay-footer": {
    position: "sticky",
    bottom: "-32px",
    left: "-32px",
    right: "-32px",
    width: "calc(100% + 64px)",
    height: "100px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "12px",
    borderTop: "1px solid $gray400",
    backgroundColor: "$wash",
    padding: "12px",
    marginTop: "auto",
    marginBottom: "-32px",
  },

  [`.${darkTheme.className} &`]: {
    backgroundColor: "$darkwash",
    color: "$text",
    "& .x-button": { filter: "invert(1)" },
    "& .form-section label": { color: "$text" },
    "& .form-section textarea, & .card-search, & .selected-card .position-input": { backgroundColor: "#000", color: "$text", borderColor: "#505050", "&:focus": { borderColor: "#aaa" } },
    "& .selected-card .card-preview img": { borderColor: "#505050" },
    "& .selected-card .card-preview .remove-card": { backgroundColor: "#fff", color: "#000" },
    "& .selected-card .position-display": { color: "rgba(255,255,255,0.6)" },
    "& .selected-card .reverse-button img, & .clear-icon": { filter: "invert(1)" },
    "& .card-option": { backgroundColor: "#000", borderColor: "rgba(255,255,255,0.25)", color: "$text", "&:hover:not(:disabled)": { backgroundColor: "#262626", borderColor: "rgba(255,255,255,0.25)" } },
    "& .suit-button": { backgroundColor: "#383838", color: "rgba(255,255,255,0.8)", borderColor: "rgba(255,255,255,0.05)", "&:hover": { backgroundColor: "#4a4a4a" }, "&.active": { backgroundColor: "#fff", color: "#000" } },
    "& .overlay-footer": { backgroundColor: "$darkwash", borderTopColor: "$gray600" },
  },

  variants: {
    isIOSNative: {
      true: {
        "& .overlay-footer": {
          height: "auto",
          paddingBottom: "50px",
          paddingTop: "24px",
        },
      },
    },
  },
});